// Accordion — collapsible section (PSD §10.3). Presentation.
//
// Stage 6f /animate: open/close is now a calm transform+opacity reveal rather
// than an instant cut. The body fades + lifts on open and reverses on close;
// the chevron rotates to match. Height collapse is a single instant step that
// happens AFTER the close fade completes (kept out of the animation path so no
// layout property is ever animated — only opacity and transform transition).
// prefers-reduced-motion disables the transitions via global.css.

import { useState, useRef, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface AccordionProps {
  title: string;
  defaultOpen?: boolean;
  badge?: ReactNode;
  children: ReactNode;
}

export function Accordion({ title, defaultOpen = false, badge, children }: AccordionProps) {
  // `open` drives aria-expanded, the chevron rotation, and the body fade.
  // `collapsed` controls whether the body is mounted. On close we keep the
  // body mounted long enough for the fade-out to finish, then collapse it.
  const [open, setOpen] = useState(defaultOpen);
  const [collapsed, setCollapsed] = useState(!defaultOpen);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function toggle() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (!open) {
      // Opening: mount the body (hidden), then reveal it next frame so the
      // transition has a starting state to animate from.
      setCollapsed(false);
      requestAnimationFrame(() => setOpen(true));
    } else {
      // Closing: fade out first, collapse after the transition finishes.
      setOpen(false);
      closeTimer.current = setTimeout(() => setCollapsed(true), 200);
    }
  }

  return (
    <div className={`accordion${open ? ' accordion--open' : ''}`}>
      <button
        type="button"
        className="accordion-header"
        aria-expanded={open}
        onClick={toggle}
      >
        <span>{title}</span>
        <span className="row-2">
          {badge}
          <ChevronDown className="accordion-chevron" aria-hidden="true" size={16} />
        </span>
      </button>
      {!collapsed && (
        <div className="accordion-body" aria-hidden={!open}>
          {children}
        </div>
      )}
    </div>
  );
}
